import type { BriefQueuedRow, RunState } from '@ni/shared';
import { isTerminal } from './run-state';

/**
 * Which surface each open question renders on — the one rule, in one pure
 * function, because it is an **invariant** rather than a rendering preference.
 *
 * Home counts pending non-blocking adjudications on the subject alone. So every
 * row the brief returns has to land somewhere the office can answer it, and the
 * two buckets below are complements by construction: a row is inline when the
 * page is already drawing the fact it gates, and flagged otherwise. Nothing is
 * dropped, and — the part that used to be wrong — nothing is dropped by being
 * *overwritten*.
 *
 * The partition is keyed on **decision id, never lineage id**. Two pending
 * questions can name one lineage (a re-gate on a fact that already carried an
 * open row), and the inline surface can only draw one of them: it is a map from
 * lineage to the row rendering at that fact. Partitioning by lineage therefore
 * lost the sibling to a `Map.set` — inline showed one, flagged excluded both as
 * "already inline", and Home went on counting two. The inline set is the set of
 * decision ids actually placed, and the complement is taken against that.
 *
 * There is a **third** surface, and it is why the buckets can both be empty for
 * a row: a run parked at `awaiting_adjudication` asks its own questions in the
 * run strip's review panel (§3f). Those rows belong to that panel alone — the
 * page must not also draw them at their facts, which is one question asked twice
 * on one screen with two sets of buttons. Everything the panel is not showing
 * belongs to this page, and every row belongs to exactly one of the three.
 */
export interface QueuedPartition {
  /** `lineage_id → decision_id`, for the rows that render at their own fact row. */
  readonly inlineByLineage: Map<string, string>;
  /** Exactly the decision ids `inlineByLineage` is showing. */
  readonly inlineDecisionIds: Set<string>;
  /** Everything else the server returned, minus the live run's own questions. */
  readonly flagged: BriefQueuedRow[];
}

/**
 * A pending question the **record** knows about — `Fact.pending_adjudication`,
 * which carries a decision id and no run.
 */
export interface RecordPendingRow {
  readonly lineage_id: string;
  readonly decision_id: string;
}

export function partitionQueuedRows(
  rows: readonly BriefQueuedRow[],
  /** Lineages the record is rendering as active — the only rows that can go inline. */
  activeLineages: ReadonlySet<string>,
  /**
   * The subject's latest run, from the record rather than the polled read, so
   * the partition is stable from the first paint.
   *
   * Its questions are withheld from this section **only while it is
   * non-terminal** — working, checkpointed, or parked at an `awaiting_*` state.
   * That is Home's rule, not this page's: `needs-action`'s `parkOf` withholds a
   * question from the "review it on the brief" line exactly when its run is one
   * the office is still waiting on, and a run that is over is not. A parked run
   * asks its questions in the run strip's review panel and a working run's are
   * pinned out of the dossier, so either way offering them here would be a
   * second copy of the same ask.
   *
   * A **failed** run is terminal and its rows belong here. The page keeps a
   * failed run "live" in one narrower sense — `research.tsx`'s `liveRunId`, so
   * the strip mounts its Resume — and keying this on that was the one hole this
   * section left open: the review panel mounts only at `awaiting_adjudication`,
   * so a failed run's questions were withheld from the section, shown by no
   * panel, and still counted on Home. Complete and withdrawn runs are terminal
   * for the same reason. Rows carrying no run at all are nobody's live work and
   * always stay.
   */
  latestRun: { id: string; state: RunState } | null,
  /**
   * The record's own pending flags, which cover the same ground as the brief's
   * rows and are the only source when the brief has not landed yet. The caller
   * passes an empty list on the **pinned** view, where the brief withholds the
   * live run's questions on purpose and a second source would put them back.
   *
   * They carry a decision id and no run, so a parked run's rows cannot be told
   * apart here — which is why the whole fallback stands down while a run is
   * parked rather than being filtered row by row. It loses nothing: the parked
   * view the fallback runs on is the draft, and there the brief already carries
   * every pending row for the subject.
   */
  recordPending: readonly RecordPendingRow[] = [],
): QueuedPartition {
  const withheldRunId = latestRun !== null && !isTerminal(latestRun.state) ? latestRun.id : null;

  /* The run strip's review panel mounts at exactly this state and lists this
     run's questions in full (`run-strip.tsx` → `FindingReview`). While it does,
     those rows are its own: drawn here as well, one question would carry two
     Keep buttons on one screen, and answering either would silently settle the
     other. Narrow on purpose — a `running` or `checkpointed` run has no panel,
     so its rows keep the inline home they have always had. */
  const parkedRunId = latestRun?.state === 'awaiting_adjudication' ? latestRun.id : null;
  const parked = (row: BriefQueuedRow): boolean =>
    parkedRunId !== null && row.run_id === parkedRunId;

  const inlineByLineage = new Map<string, string>();
  const inlineDecisionIds = new Set<string>();

  for (const row of rows) {
    if (parked(row)) continue;
    if (!row.lineage_id || !activeLineages.has(row.lineage_id)) continue;
    // First row on a lineage takes the inline slot; a sibling falls through to
    // the flagged section rather than off the page.
    if (inlineByLineage.has(row.lineage_id)) continue;
    inlineByLineage.set(row.lineage_id, row.decision_id);
    inlineDecisionIds.add(row.decision_id);
  }

  if (parkedRunId === null) {
    for (const entry of recordPending) {
      if (inlineByLineage.has(entry.lineage_id)) continue;
      inlineByLineage.set(entry.lineage_id, entry.decision_id);
      inlineDecisionIds.add(entry.decision_id);
    }
  }

  const flagged = rows.filter(
    (row) =>
      !inlineDecisionIds.has(row.decision_id) &&
      !(withheldRunId !== null && row.run_id === withheldRunId),
  );

  return { inlineByLineage, inlineDecisionIds, flagged };
}
